import type { Record } from "@suse-o11y/record";
import { toEntries } from "@suse-o11y/to-entries";

import { Denied } from "./denied";
import { Granted } from "./granted";

export type { Denied } from "./denied";
export type { Granted } from "./granted";

type GrantedAllShape<TShape extends Record<string, Permitted<unknown>>> = {
	[TKey in keyof TShape]: TShape[TKey] extends Permitted<infer TValue> ? TValue : never;
};

/**
 * A monad representing the result of a permissions check.
 *
 * Semantically equivalent to Maybe/Optional, but conveys access-control intent:
 * a {@link Granted} instance carries the value the caller is permitted to use,
 * while a {@link Denied} instance signals that access was denied and no value is available.
 *
 * @example
 * ```ts
 * const view: Permitted<View> = canRead
 *   ? Permitted.granted(view)
 *   : Permitted.denied();
 *
 * // Extract the value with a fallback
 * const result = view.getOrElse(fallbackView);
 *
 * // Narrow with type guards
 * if (view.isGranted()) {
 *   console.log(view.value);
 * }
 * ```
 *
 * @template TValue - The type of the value available when access is granted.
 */
type Permitted<TValue> = Denied | Granted<TValue>;

const DENIED = new Denied();

/**
 * Returns the exact {@link Denied} class instance, representing {@link Permitted<never>}.
 *
 * Use this overload when the calling context explicitly defines the {@link Permitted} `TValue`.
 *
 * @example
 * function (input: string): Permitted<string> {
 *   if (input === 'denied'){
 *     return Permitted.denied()
 *   }
 *
 *   return Permitted.granted(input);
 * }
 *
 * @returns The {@link Denied} singleton.
 */
function denied(): Denied;

/**
 * Returns an {@link Permitted<TValue>} instance.
 *
 * Use this overload when call site can't infer desired {@link TValue} without explicit annotation.
 *
 * @example
 * // Allows use of both Permitted.granted andPermitted.denied
 * const permitted = Signal(Permitted.denied<string>()); // Signal<Permitted<string>>
 *
 * // Allows only Permitted.denied
 * const denied = Signal(Permitted.denied());// Denied
 *
 * @template TValue - The phantom type to align with the expected {@link Permitted<TValue>} variant.
 * @returns A {@link Permitted<TValue>} instance.
 */
function denied<TValue>(): Permitted<TValue>;

function denied<TValue = never>(): Permitted<TValue> {
	return DENIED;
}

/**
 * Returns the exact {@link Granted} class instance wrapping the given value.
 *
 * Use this overload when the call site can infer the desired {@link TValue} from the provided {@link value}.
 *
 * @remark The {@link _ForceTypeInference} phantom type is a workaround to distinguish this overload from
 * explicit annotation of the widened overload, enabling inference of {@link TValue} from the provided {@link value} without ambiguity.
 *
 * @example
 * function (input: string): Permitted<string> {
 *   if (input === 'denied') {
 *     return Permitted.denied();
 *   }
 *
 *   return Permitted.granted(input);
 * }
 *
 * @template TValue - The type of the wrapped value (inferred).
 * @template _ForceTypeInference - An unused phantom type to enable inference of {@link TValue} from the provided {@link value}.
 * @param value - The value to wrap.
 * @returns A {@link Granted} instance containing the value.
 */
function granted<TValue, _ForceTypeInference extends never>(value: TValue): Granted<TValue>;

/**
 * Returns an {@link Permitted<TValue>} instance wrapping the given {@link value}.
 *
 * Use this overload when call site can't infer desired {@link TValue} without explicit annotation.
 *
 * @example
 * // Allows use of both Permitted.granted and Permitted.denied
 * const permitted = Signal(Permitted.granted<string>("hello")); // Signal<Permitted<string>>
 *
 * // Allows only Permitted.granted
 * const granted = Signal(Permitted.granted("hello")); // Granted<string>
 *
 * @template TValue - The type of the wrapped {@link value}.
 * @param value - The value to wrap.
 * @returns A {@link Permitted<TValue>} instance.
 */
function granted<TValue>(value: TValue): Permitted<TValue>;

function granted<TValue>(value: TValue): Permitted<TValue> {
	return new Granted(value);
}

/**
 * Combines a record of {@link Permitted} values into a single {@link Permitted} of a record.
 * Returns {@link Granted} with all unwrapped values if every entry is granted,
 * or {@link Denied} if any entry is denied.
 *
 * @template TShape - A record type where every value is a {@link Permitted}.
 * @param shape - The record of {@link Permitted} values to combine.
 * @returns A {@link Granted} containing the unwrapped record, or {@link Denied} if any entry is denied.
 */
function all<TShape extends Record<string, Permitted<unknown>>>(shape: TShape): Permitted<GrantedAllShape<TShape>> {
	const result = {} as GrantedAllShape<TShape>;

	for (const [key, permitted] of toEntries(shape)) {
		if (!permitted.isGranted()) {
			return DENIED;
		}

		result[key] = permitted.value as GrantedAllShape<TShape>[keyof TShape];
	}

	return new Granted(result);
}

const Permitted = { denied, granted, all };

export type { GrantedAllShape };
export { Permitted };
